/**
 * Blocked users management page.
 * Lists blocked users and allows blocking/unblocking.
 */

import { Head, Link, router } from '@inertiajs/react';
import { useState } from 'react';
import { FlashMessage } from '@/components/ui/FlashMessage';

/** Blocked user data */
interface BlockedUser {
    id: number;
    username: string;
    blocked_at: string;
}

/** Pagination link */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/** Paginated response from Laravel */
interface PaginatedBlockedUsers {
    data: BlockedUser[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    links: PaginationLink[];
}

interface Props {
    blockedUsers: PaginatedBlockedUsers;
}

/** Format date in a readable format */
function formatDate(isoString: string): string {
    return new Date(isoString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

/**
 * Blocks page - manage blocked users for missives.
 */
export default function Blocks({ blockedUsers }: Props) {
    const [username, setUsername] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    /** Handle block user */
    const handleBlock = (e: React.FormEvent) => {
        e.preventDefault();

        if (!username.trim()) {
            setError('Username is required');
            return;
        }

        setIsSubmitting(true);
        setError('');

        router.post('/missives/blocks', { username: username.trim() }, {
            onError: (errors) => {
                setError(errors.username || 'Failed to block user');
                setIsSubmitting(false);
            },
            onSuccess: () => {
                setUsername('');
            },
            onFinish: () => {
                setIsSubmitting(false);
            },
        });
    };

    /** Handle unblock user */
    const handleUnblock = (userId: number) => {
        router.delete(`/missives/blocks/${userId}`);
    };

    return (
        <>
            <Head title="Blocked Users" />
            <FlashMessage />

            <div className="min-h-screen bg-slate-900 text-white">
                <div className="max-w-2xl mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-8">
                        <Link
                            href="/missives"
                            className="text-slate-300 hover:text-white transition-colors"
                        >
                            ← Back to Missives
                        </Link>
                        <h1 className="text-3xl font-bold">🚫 Blocked Users</h1>
                    </div>

                    {/* Block User Form */}
                    <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 mb-8">
                        <h2 className="text-lg font-medium mb-4">Block a User</h2>
                        <p className="text-sm text-slate-400 mb-4">
                            Blocked users cannot send you missives and you cannot send missives to them.
                        </p>
                        <form onSubmit={handleBlock} className="flex gap-3">
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Enter username to block..."
                                className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-800 rounded-lg font-medium transition-colors"
                            >
                                {isSubmitting ? 'Blocking...' : 'Block'}
                            </button>
                        </form>
                        {error && (
                            <p className="mt-2 text-sm text-red-400">{error}</p>
                        )}
                    </div>

                    {/* Blocked Users List */}
                    <div className="bg-slate-800 rounded-lg border border-slate-700">
                        <div className="p-4 border-b border-slate-700">
                            <h2 className="text-lg font-medium">
                                Blocked Users ({blockedUsers.total})
                            </h2>
                        </div>

                        {blockedUsers.data.length === 0 ? (
                            <div className="p-8 text-center text-slate-400">
                                <span className="text-3xl mb-2 block">✓</span>
                                <p>You haven't blocked anyone.</p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-slate-700">
                                {blockedUsers.data.map((user) => (
                                    <li key={user.id} className="p-4 flex items-center justify-between">
                                        <div>
                                            <span className="font-medium text-white">{user.username}</span>
                                            <span className="text-sm text-slate-500 ml-3">
                                                Blocked on {formatDate(user.blocked_at)}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleUnblock(user.id)}
                                            className="px-3 py-1 bg-slate-600 hover:bg-slate-500 rounded text-sm transition-colors"
                                        >
                                            Unblock
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Pagination */}
                        {blockedUsers.last_page > 1 && (
                            <div className="p-4 border-t border-slate-700 flex justify-center gap-2">
                                {blockedUsers.links.map((link, index) => (
                                    <Link
                                        key={index}
                                        href={link.url || '#'}
                                        className={`px-3 py-1 rounded text-sm transition-colors ${
                                            link.active
                                                ? 'bg-amber-600 text-white'
                                                : link.url
                                                    ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                                                    : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                                        }`}
                                        dangerouslySetInnerHTML={{ __html: link.label }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

